import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import _ from "lodash";

import { Box, BlueText, ArrowIcon } from "components/common";
import { ACTION_FAIL_MESSAGE, ConfigLimit, LDSUTabPanel } from "constant";
import { Registry } from "types/Gateways";
import styledConst from "styles";
import { useModal } from "components/modals/ModalTemplate";
import { Form, InputGroup, Button } from "react-bootstrap";
import { updateDeviceConfigByPort } from "service/gatewayService";
import { getAPIError, showSuccessAlert, showErrorAlert } from "utils/alert";
import { isHttpSuccess } from "utils/functions";

const StyledContainer = styled.div`
    display: flex;
    padding: 17px 30px;
    align-items: flex-end;
    gap: 17px;

    > div {
        flex: 1;
    }

    .gateway-name {
        gap: 10px;
        display: flex;
        align-items: center;

        .icon {
            width: 36px;
            height: 36px;
            border-radius: 50%;
        }
    }

    .label {
        color: ${styledConst.Primary_Blue_4};
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 5px;
    }

    a,
    .sensor-value {
        color: #00cbaf;
    }

    .action {
        display: flex;
        justify-content: space-between;
        align-items: center;

        > div {
        }

        .custom-switch {
            padding-left: 2.25rem; // Rollback to default padding of bootstrap
        }

        .label > span:first-child {
            position: relative;
            top: 2px;
        }
    }
`;

const NoteText = styled.div`
    font-size: 16px;
    color: ${styledConst.Primary_Blue_4};

    .note-label {
        color: #fff;
    }
`;

const LinkBox = ({ children, to, state, onClick }: any) => {
    return (
        <Box className="d-flex flex-fill">
            <div>{children}</div>
            <Link
                to={{ pathname: to, state }}
                className="ml-auto"
                onClick={(e) => {
                    if (to === "#" && onClick) {
                        e.preventDefault();
                        onClick(e);
                    }
                }}
            >
                <ArrowIcon className="material-icons align-middle">
                    keyboard_arrow_right
                </ArrowIcon>
            </Link>
        </Box>
    );
};

const LoRaWanGatewayDetail = ({
    LDSUPowerState = [],
    gateway,
    onChange,
}: {
    LDSUPowerState: any[];
    gateway: Registry;
    onChange?: () => void;
}) => {
    const isValidIntegerReportRate = (value: unknown) => {
        if (typeof value === "number") {
            return Number.isInteger(value) && value >= 0;
        }

        if (typeof value !== "string") {
            return false;
        }

        return /^\d+$/.test(value.trim());
    };

    const {
        openModal,
        modal: ActionModal,
        setModalShow: setActionModalShow,
        setModalData,
    } = useModal();

    const ldsus = _.flatten(_.map(LDSUPowerState, "info.ldsus"));
    const sensors = _.flatten(_.map(LDSUPowerState, "info.sensors"));
    const actuators = _.flatten(_.map(LDSUPowerState, "info.actuators"));
    const reportRate = ldsus?.[0]?.INTVL;

    const changeReportRateHandler = () => {
        setModalData({
            reportRate,
        });

        const modalContent = (openedModalData: any) => {
            const numericReportRate = Number(openedModalData.reportRate);
            const isReportRateInvalid =
                !isValidIntegerReportRate(openedModalData.reportRate) ||
                numericReportRate % 5 !== 0 ||
                numericReportRate < ConfigLimit.LORAWAN_REPORT_RATE_MIN ||
                numericReportRate > ConfigLimit.SENSOR_REPORT_RATE_MAX;

            return (
                <Form>
                    <Form.Group className="text-gray input-right-text">
                        <Form.Label className="text-left w-100">
                            Report Rate
                        </Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                name="reportRate"
                                value={openedModalData.reportRate}
                                isInvalid={isReportRateInvalid}
                                onChange={(e) => {
                                    const nextReportRate =
                                        e.currentTarget.value.trim();
                                    if (
                                        nextReportRate &&
                                        !isValidIntegerReportRate(
                                            nextReportRate
                                        )
                                    ) {
                                        return;
                                    }

                                    setModalData({
                                        reportRate: nextReportRate,
                                    });
                                }}
                                aria-label="input report rate"
                            />
                            <span>sec(s)</span>
                        </InputGroup>
                    </Form.Group>
                    <p className="w-100 text-left fs-14">
                        {`Minimum - ${ConfigLimit.LORAWAN_REPORT_RATE_MIN} seconds (5 minutes), Maximum - ${ConfigLimit.SENSOR_REPORT_RATE_MAX} seconds (30 minutes) and value should be multiple of 5 seconds.`}
                    </p>
                </Form>
            );
        };

        const modalFooter = (openedModalData: any) => {
            const numericReportRate = Number(openedModalData.reportRate);
            const isReportRateInvalid =
                !isValidIntegerReportRate(openedModalData.reportRate) ||
                numericReportRate % 5 !== 0 ||
                numericReportRate < ConfigLimit.LORAWAN_REPORT_RATE_MIN ||
                numericReportRate > ConfigLimit.SENSOR_REPORT_RATE_MAX;

            return (
                <div className="w-100 d-flex justify-content-end align-items-center m-0">
                    <Button
                        disabled={isReportRateInvalid}
                        onClick={async () => {
                            try {
                                const res = await updateDeviceConfigByPort(
                                    gateway.gateway_id,
                                    String(1),
                                    {
                                        INTVL: Number(
                                            openedModalData.reportRate
                                        ),
                                    }
                                );

                                if (!isHttpSuccess(res?.status)) {
                                    showErrorAlert(
                                        getAPIError(res, ACTION_FAIL_MESSAGE)
                                    );
                                    return;
                                }

                                showSuccessAlert({
                                    message:
                                        "Report rate has been updated successfully.",
                                });
                                onChange?.();
                                setActionModalShow(false);
                            } finally {
                                // Intentionally left blank
                            }
                        }}
                    >
                        UPDATE
                    </Button>
                </div>
            );
        };

        openModal({
            key: "change_report_rate",
            modalType: "content",
            modalTitle: "Change Report Rate",
            width: 635,
            modalContent,
            modalFooter,
        });
    };

    return (
        <>
            <div>
                <StyledContainer className="form-box float-none">
                    <NoteText>
                        <span className="note-label">Note:</span>
                        <span className="ml-2">
                            Sensor/Actuator configuration with a duration
                            shorter than the report rate interval will not
                            function as expected.
                        </span>
                    </NoteText>
                </StyledContainer>
            </div>
            <div className="mt-2">
                <div className="d-flex flex-column flex-lg-row gap-2">
                    <StyledContainer className="form-box">
                        <div>
                            <div className="mb-3">Modbus</div>
                            <LinkBox
                                to={`/gateway-details/${gateway.gateway_id}/LDSBus/1`}
                                state={{
                                    gatewayName: gateway.name,
                                    panel: LDSUTabPanel.LDSU,
                                }}
                            >
                                <div>
                                    {ldsus?.length || 0}{" "}
                                    <BlueText className="ml-2 large">
                                        LDSUs
                                    </BlueText>
                                </div>
                            </LinkBox>
                        </div>
                        <div>
                            <div className="mb-3"></div>
                            <LinkBox
                                to="#"
                                state={{
                                    gatewayName: gateway.name,
                                    panel: LDSUTabPanel.LDSU,
                                }}
                                onClick={() => {
                                    changeReportRateHandler();
                                }}
                            >
                                <div>
                                    {reportRate ?? "-"} sec(s)
                                    <BlueText className="ml-2 large">
                                        Reporting Rate
                                    </BlueText>
                                </div>
                            </LinkBox>
                        </div>
                    </StyledContainer>
                    <StyledContainer className="form-box">
                        <div>
                            <div className="mb-3">
                                Sensors and Actuators reported from connected
                                LDSUs
                            </div>
                            <div className="d-flex flex-column flex-sm-row gap-3">
                                <LinkBox
                                    to={`/gateway-details/${gateway.gateway_id}/LDSBus/1`}
                                    state={{
                                        gatewayName: gateway.name,
                                        portNumber: "1",
                                        UUID: gateway.gateway_id,
                                        panel: LDSUTabPanel.Sensor,
                                    }}
                                >
                                    <div>
                                        {sensors?.length || 0}{" "}
                                        <BlueText className="ml-2 large">
                                            Sensors
                                        </BlueText>
                                    </div>
                                </LinkBox>

                                <LinkBox
                                    to={`/gateway-details/${gateway.gateway_id}/LDSBus/1`}
                                    state={{
                                        gatewayName: gateway.name,
                                        portNumber: "1",
                                        UUID: gateway.gateway_id,
                                        panel: LDSUTabPanel.Actuator,
                                    }}
                                >
                                    <div>
                                        {actuators?.length || 0}{" "}
                                        <BlueText className="ml-2 large">
                                            Actuators
                                        </BlueText>
                                    </div>
                                </LinkBox>
                            </div>
                        </div>
                    </StyledContainer>
                </div>
            </div>
            {ActionModal}
        </>
    );
};

export default LoRaWanGatewayDetail;
